import { useState, useEffect } from 'react';
import {
  GitBranch,
  Play,
  Loader2,
  Plus,
  Trash2,
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart3,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import clsx from 'clsx';
import type { ScenarioResult, Entity } from '@/types';
import { scenarioApi, entitiesApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Helpers -----------------------------------------------------------------

function formatAmount(amount: number): string {
  if (amount === 0) return '\u2014';
  const abs = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(abs);
  return amount < 0 ? `(${formatted})` : formatted;
}

// --- Scenario Types ----------------------------------------------------------

const scenarioTypes = [
  { id: 'acquisition', label: 'Acquisition', description: 'Model acquiring a new entity' },
  { id: 'disposal', label: 'Disposal', description: 'Model disposing of an entity' },
  { id: 'fx_shock', label: 'FX Shock', description: 'Model exchange rate changes' },
  { id: 'revenue_change', label: 'Revenue Change', description: 'Model revenue variations' },
  { id: 'custom', label: 'Custom', description: 'Custom adjustments' },
];

// --- Custom Adjustment Row ---------------------------------------------------

interface AdjustmentRow {
  account: string;
  entity: string;
  amount: string;
}

// --- Chart Colors ------------------------------------------------------------

const COLORS = { base: '#94a3b8', scenario: '#3b82f6' };

// --- Main Page ---------------------------------------------------------------

export default function ScenarioModeling() {
  const { addNotification } = useAppStore();
  const [entities, setEntities] = useState<Entity[]>([]);

  // Scenario configuration
  const [scenarioType, setScenarioType] = useState('acquisition');
  const [scenarioName, setScenarioName] = useState('');
  const [running, setRunning] = useState(false);

  // Type-specific fields
  const [acquiEntityName, setAcquiEntityName] = useState('');
  const [acquiOwnership, setAcquiOwnership] = useState('100');
  const [acquiConsideration, setAcquiConsideration] = useState('');
  const [acquiFairValues, setAcquiFairValues] = useState('');

  const [disposalEntity, setDisposalEntity] = useState('');
  const [disposalPct, setDisposalPct] = useState('100');
  const [disposalProceeds, setDisposalProceeds] = useState('');

  const [fxCurrencies, setFxCurrencies] = useState<string[]>(['EUR']);
  const [fxChangePct, setFxChangePct] = useState('');

  const [revEntities, setRevEntities] = useState<string[]>([]);
  const [revChangePct, setRevChangePct] = useState('');

  const [customAdjustments, setCustomAdjustments] = useState<AdjustmentRow[]>([
    { account: '', entity: '', amount: '' },
  ]);

  // Results
  const [result, setResult] = useState<ScenarioResult | null>(null);
  const [savedScenarios, setSavedScenarios] = useState<ScenarioResult[]>([]);

  // Sensitivity
  const sensitivityPcts = [-20, -10, -5, 0, 5, 10, 20];

  useEffect(() => {
    entitiesApi.list({ page: 1, page_size: 100 }).then((res) => setEntities(res.items)).catch(() => {});
    scenarioApi.list().then(setSavedScenarios).catch(() => {});
  }, []);

  const buildParameters = (): Record<string, any> => {
    switch (scenarioType) {
      case 'acquisition':
        return {
          entity_name: acquiEntityName,
          ownership_pct: parseFloat(acquiOwnership) || 0,
          consideration: parseFloat(acquiConsideration) || 0,
          fair_values: acquiFairValues,
        };
      case 'disposal':
        return {
          entity_id: disposalEntity,
          disposal_pct: parseFloat(disposalPct) || 0,
          proceeds: parseFloat(disposalProceeds) || 0,
        };
      case 'fx_shock':
        return {
          currencies: fxCurrencies,
          change_pct: parseFloat(fxChangePct) || 0,
        };
      case 'revenue_change':
        return {
          entity_ids: revEntities,
          change_pct: parseFloat(revChangePct) || 0,
        };
      case 'custom':
        return {
          adjustments: customAdjustments
            .filter((a) => a.account && a.amount)
            .map((a) => ({
              account: a.account,
              entity: a.entity,
              amount: parseFloat(a.amount) || 0,
            })),
        };
      default:
        return {};
    }
  };

  const handleRun = async () => {
    setRunning(true);
    setResult(null);
    try {
      const res = await scenarioApi.run({
        scenario_type: scenarioType,
        name: scenarioName || `${scenarioType} scenario`,
        parameters: buildParameters(),
      });
      setResult(res);
      addNotification({ type: 'success', title: 'Scenario Complete', message: 'Scenario analysis has been completed.' });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Scenario Failed', message: apiErr.detail || 'Failed to run scenario.' });
    } finally {
      setRunning(false);
    }
  };

  const addCustomRow = () => {
    setCustomAdjustments((prev) => [...prev, { account: '', entity: '', amount: '' }]);
  };

  const removeCustomRow = (index: number) => {
    setCustomAdjustments((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCustomRow = (index: number, field: keyof AdjustmentRow, value: string) => {
    setCustomAdjustments((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  // Build comparison chart data
  const comparisonData = result
    ? result.impact_summary.map((item) => ({
        metric: item.metric,
        Base: item.base_value,
        Scenario: item.adjusted_value,
      }))
    : [];

  // Build sensitivity data
  const sensitivityData = result && result.impact_summary.length > 0
    ? sensitivityPcts.map((pct) => {
        const row: Record<string, any> = { pct: `${pct > 0 ? '+' : ''}${pct}%` };
        result.impact_summary.slice(0, 3).forEach((item) => {
          row[item.metric] = item.base_value * (1 + pct / 100);
        });
        return row;
      })
    : [];

  const metricKeys = result ? result.impact_summary.slice(0, 3).map((i) => i.metric) : [];
  const lineColors = ['#3b82f6', '#8b5cf6', '#10b981'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
          <GitBranch className="w-5 h-5 text-cyan-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scenario Modeling</h1>
          <p className="text-sm text-gray-500 mt-0.5">What-if analysis for financial impact assessment</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration Panel */}
        <div className="lg:col-span-1 space-y-4">
          {/* Scenario Type Selector */}
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Scenario Type</h2>
            <div className="space-y-2">
              {scenarioTypes.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setScenarioType(t.id)}
                  className={clsx(
                    'w-full text-left px-3 py-2.5 rounded-lg border transition-colors',
                    scenarioType === t.id
                      ? 'border-cyan-300 bg-cyan-50'
                      : 'border-gray-200 hover:bg-gray-50',
                  )}
                >
                  <p className={clsx('text-sm font-medium', scenarioType === t.id ? 'text-cyan-800' : 'text-gray-700')}>
                    {t.label}
                  </p>
                  <p className="text-xs text-gray-500">{t.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Dynamic Form */}
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Parameters</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Scenario Name</label>
                <input
                  className="input-field text-sm"
                  value={scenarioName}
                  onChange={(e) => setScenarioName(e.target.value)}
                  placeholder="Name this scenario..."
                />
              </div>

              {scenarioType === 'acquisition' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Entity Name</label>
                    <input className="input-field text-sm" value={acquiEntityName} onChange={(e) => setAcquiEntityName(e.target.value)} placeholder="New entity name" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Ownership %</label>
                    <input type="number" className="input-field text-sm" value={acquiOwnership} onChange={(e) => setAcquiOwnership(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Consideration Amount</label>
                    <input type="number" className="input-field text-sm" value={acquiConsideration} onChange={(e) => setAcquiConsideration(e.target.value)} placeholder="0" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Fair Values (JSON)</label>
                    <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono resize-none focus:ring-2 focus:ring-primary-500" rows={3} value={acquiFairValues} onChange={(e) => setAcquiFairValues(e.target.value)} placeholder='{"assets": 0, "liabilities": 0}' />
                  </div>
                </>
              )}

              {scenarioType === 'disposal' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Entity</label>
                    <select className="select-field text-sm" value={disposalEntity} onChange={(e) => setDisposalEntity(e.target.value)}>
                      <option value="">Select entity</option>
                      {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Disposal %</label>
                    <input type="number" className="input-field text-sm" value={disposalPct} onChange={(e) => setDisposalPct(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Proceeds Amount</label>
                    <input type="number" className="input-field text-sm" value={disposalProceeds} onChange={(e) => setDisposalProceeds(e.target.value)} placeholder="0" />
                  </div>
                </>
              )}

              {scenarioType === 'fx_shock' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Currencies</label>
                    <select
                      className="select-field text-sm"
                      multiple
                      value={fxCurrencies}
                      onChange={(e) => setFxCurrencies(Array.from(e.target.selectedOptions, (o) => o.value))}
                    >
                      {['EUR', 'GBP', 'JPY', 'SGD', 'CHF', 'CNY', 'INR', 'AED'].map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Change %</label>
                    <input type="number" className="input-field text-sm" value={fxChangePct} onChange={(e) => setFxChangePct(e.target.value)} placeholder="-10" />
                  </div>
                </>
              )}

              {scenarioType === 'revenue_change' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Entities</label>
                    <select
                      className="select-field text-sm"
                      multiple
                      value={revEntities}
                      onChange={(e) => setRevEntities(Array.from(e.target.selectedOptions, (o) => o.value))}
                    >
                      {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Revenue Change %</label>
                    <input type="number" className="input-field text-sm" value={revChangePct} onChange={(e) => setRevChangePct(e.target.value)} placeholder="10" />
                  </div>
                </>
              )}

              {scenarioType === 'custom' && (
                <>
                  {customAdjustments.map((adj, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input className="input-field text-sm flex-1" value={adj.account} onChange={(e) => updateCustomRow(i, 'account', e.target.value)} placeholder="Account" />
                      <input className="input-field text-sm flex-1" value={adj.entity} onChange={(e) => updateCustomRow(i, 'entity', e.target.value)} placeholder="Entity" />
                      <input type="number" className="input-field text-sm w-28" value={adj.amount} onChange={(e) => updateCustomRow(i, 'amount', e.target.value)} placeholder="Amount" />
                      <button onClick={() => removeCustomRow(i)} className="p-1 hover:bg-red-50 rounded">
                        <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                      </button>
                    </div>
                  ))}
                  <button onClick={addCustomRow} className="text-xs text-primary-600 hover:text-primary-800 font-medium flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add Row
                  </button>
                </>
              )}
            </div>

            <button
              onClick={handleRun}
              disabled={running}
              className="btn-primary w-full mt-4"
            >
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Run Scenario
            </button>
          </div>

          {/* Saved Scenarios */}
          {savedScenarios.length > 0 && (
            <div className="card p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Saved Scenarios</h2>
              <div className="space-y-2">
                {savedScenarios.slice(0, 5).map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setResult(s)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <p className="text-sm font-medium text-gray-700">{s.name || s.scenario_type}</p>
                    <p className="text-xs text-gray-400">{s.scenario_type} &middot; {s.impact_summary.length} metrics</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Results Panel */}
        <div className="lg:col-span-2 space-y-4">
          {!result && !running ? (
            <div className="card py-20 text-center">
              <BarChart3 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <h3 className="text-base font-medium text-gray-900">Configure & Run a Scenario</h3>
              <p className="text-sm text-gray-500 mt-1">
                Select a scenario type, configure parameters, and click "Run Scenario" to see impact analysis.
              </p>
            </div>
          ) : running ? (
            <div className="card py-20 text-center">
              <Loader2 className="w-10 h-10 mx-auto text-cyan-500 animate-spin mb-3" />
              <p className="text-sm text-gray-500">Running scenario analysis...</p>
            </div>
          ) : result ? (
            <>
              {/* Impact Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {result.impact_summary.map((item) => (
                  <div key={item.metric} className="card p-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{item.metric}</p>
                    <div className="flex items-end gap-2">
                      <span className="text-lg font-bold text-gray-900 font-mono">{formatAmount(item.adjusted_value)}</span>
                      <span className={clsx(
                        'flex items-center text-xs font-medium',
                        item.delta > 0 ? 'text-green-600' : item.delta < 0 ? 'text-red-600' : 'text-gray-500',
                      )}>
                        {item.delta > 0 ? <TrendingUp className="w-3 h-3 mr-0.5" /> : item.delta < 0 ? <TrendingDown className="w-3 h-3 mr-0.5" /> : <Minus className="w-3 h-3 mr-0.5" />}
                        {Number(item.delta_pct) > 0 ? '+' : ''}{Number(item.delta_pct).toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Base: {formatAmount(item.base_value)} &middot; Delta: {formatAmount(item.delta)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Side-by-Side Comparison Chart */}
              {comparisonData.length > 0 && (
                <div className="card p-6">
                  <h2 className="text-base font-semibold text-gray-900 mb-4">Base vs Scenario Comparison</h2>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={comparisonData} barGap={4}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="metric" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }} />
                      <Legend />
                      <Bar dataKey="Base" fill={COLORS.base} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Scenario" fill={COLORS.scenario} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Sensitivity Analysis Chart */}
              {sensitivityData.length > 0 && (
                <div className="card p-6">
                  <h2 className="text-base font-semibold text-gray-900 mb-4">Sensitivity Analysis</h2>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={sensitivityData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="pct" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }} />
                      <Legend />
                      {metricKeys.map((key, i) => (
                        <Line key={key} type="monotone" dataKey={key} stroke={lineColors[i % lineColors.length]} strokeWidth={2} dot={{ r: 4 }} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
